// src/main.jsx
//
// React entry point. Vite mounts <App /> into #root.
//
// Sets window.__DEV_MODE__ from import.meta.env.DEV so the existing logger
// (src/utils/logger.js) and audit modules (src/services/auditLog.js) keep
// working in both Vite and the legacy Babel-standalone HTML launcher era.
//
// ADMIN-NAV-FEATURE-TOGGLES: FeatureFlagProvider wraps the App tree so all
// components can read live feature flags via useFeatureFlags().

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.jsx";
import { ErrorBoundary } from "./components/ErrorBoundary.jsx";
import { AccessDeniedPage } from "./components/AccessDeniedPage.jsx";
import { LoginPage } from "./components/LoginPage.jsx";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext.jsx";
import { APP_NAME } from "./constants/branding.js";
import { installDemoRolePreviewFetch } from "./utils/demoRolePreview.js";

// Drive the browser-tab title from the canonical constant so index.html
// never needs to be touched when the app is renamed.
document.title = APP_NAME;

// Make build mode visible to non-Vite-aware modules.
if (typeof window !== "undefined") {
  window.__DEV_MODE__ = import.meta.env.DEV;
}
if (import.meta.env.MODE !== "production") {
  installDemoRolePreviewFetch();
}

const root = createRoot(document.getElementById("root"));

const path = window.location.pathname;

// Standalone pages rendered outside the full App shell (no auth probe, no nav rail).
if (path === "/access-denied") {
  // Server callback redirects here when an authenticated user belongs to
  // none of the configured groups.
  root.render(
    <StrictMode>
      <AccessDeniedPage />
    </StrictMode>
  );
} else if (path === "/login") {
  // App.jsx redirects here on a 401 from /api/me. The user clicks
  // "Sign in with Replit" to start the Replit OIDC flow.
  root.render(
    <StrictMode>
      <LoginPage />
    </StrictMode>
  );
} else {
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <FeatureFlagProvider>
          <App />
        </FeatureFlagProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}
